import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// The Cold Captures pool: form entries started but never submitted. Separate
// from leads until a human promotes ("Move to pipeline") one.

export function usePartialCaptures(status = 'live') {
  return useQuery({
    queryKey: ['partial-captures', status],
    queryFn: async () => {
      const { data } = await api.get('/api/partial-captures', { params: { status } });
      return Array.isArray(data) ? data : [];
    },
    staleTime: 15_000,
  });
}

export function usePartialCaptureCount() {
  return useQuery({
    queryKey: ['partial-captures', 'count'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/partial-captures/count');
        return data?.count || 0;
      } catch {
        return 0;
      }
    },
    staleTime: 30_000,
  });
}

function invalidate(qc) {
  qc.invalidateQueries({ queryKey: ['partial-captures'] });
}

export function useConvertCapture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (await api.post(`/api/partial-captures/${id}/convert`, body)).data,
    onSuccess: () => { invalidate(qc); qc.invalidateQueries({ queryKey: ['leads'] }); },
  });
}

// Bulk activate selected captures into a cold nurture: convert to cold leads
// (tagged 'cold_capture') and optionally enroll each in a WhatsApp sequence.
export function useBulkNurtureCaptures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/partial-captures/bulk-nurture', body)).data,
    onSuccess: () => { invalidate(qc); qc.invalidateQueries({ queryKey: ['leads'] }); },
  });
}

export function useDismissCapture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/partial-captures/${id}/dismiss`)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useMarkCaptureContacted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status = 'contacted' }) => (await api.patch(`/api/partial-captures/${id}`, { status })).data,
    onSuccess: () => invalidate(qc),
  });
}

// ─── Working a capture: activity, notes, WhatsApp, email ────────────────────
export function useCaptureActivity(id) {
  return useQuery({
    queryKey: ['partial-capture-activity', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/partial-captures/${id}/activity`);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id,
    staleTime: 5_000,
  });
}

function afterActivity(qc, id) {
  qc.invalidateQueries({ queryKey: ['partial-capture-activity', id] });
  qc.invalidateQueries({ queryKey: ['partial-captures'] });
}

export function useAddCaptureNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/api/partial-captures/${id}/note`, { body })).data,
    onSuccess: (_, v) => afterActivity(qc, v.id),
  });
}

export function useSendCaptureWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (await api.post(`/api/partial-captures/${id}/whatsapp`, body)).data,
    onSuccess: (_, v) => afterActivity(qc, v.id),
  });
}

export function useSendCaptureEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (await api.post(`/api/partial-captures/${id}/email`, body)).data,
    onSuccess: (_, v) => afterActivity(qc, v.id),
  });
}

// Email "From" identities (reuses the lead composer's sender list).
export function useCaptureEmailSenders() {
  return useQuery({
    queryKey: ['lead-email-senders'],
    queryFn: async () => {
      const { data } = await api.get('/api/lead-email/senders');
      return data || { senders: [], can_send: false };
    },
    staleTime: 30_000,
  });
}
